// redux/authSlice.js
import { createSlice } from '@reduxjs/toolkit';

// Initial state for the auth
const initialState = {
  user: null,             // User data will be set here once logged in
  loading: false,         // Indicates whether a request is ongoing (e.g., login request)
  error: null,            // Error state for handling login failures
  events: [],             // NEW: Stores list of events from login response
  selectedEvent: null,    // NEW: Stores the selected event
};

// Create slice with reducers for login process
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginRequest: (state) => {
      state.loading = true;
      state.error = null; // Reset error on new login attempt
    },
    loginSuccess: (state, action) => {
      if (action.payload.user) {
        state.user = action.payload.user;       // NEW STRUCTURE: Expecting { user, events } format
        state.events = action.payload.events || [];
      } else {
        state.user = action.payload;            // Fallback: original structure (in case only user is passed)
      }
      state.loading = false;
      state.error = null;
    },
    loginFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    logout: (state) => {
      state.user = null;
      state.events = [];           // Clear events
      state.selectedEvent = null;  // Clear selected event
      state.loading = false;
      state.error = null;
    },
    updateUser: (state, action) => {
      state.user = action.payload;
    },
    
    // NEW: Set selected event
    setSelectedEvent: (state, action) => {
      const next = action.payload || null;
      state.selectedEvent = next;
      if (next?.eventId) {
        const id = String(next.eventId);
        const list = state.events || [];
        const idx = list.findIndex((event) => String(event.eventId) === id);
        if (idx >= 0) {
          state.events[idx] = { ...list[idx], ...next };
        } else {
          state.events = [...list, next];
        }
      }
      // Keep Super Admin identity; sync role for event users only
      if (state.user && !state.user.isSuperAdmin) {
        if (next?.role) {
          state.user = { ...state.user, role: next.role };
        } else if (next?.isAdmin) {
          state.user = { ...state.user, role: "Admin" };
        }
      }
    },

    /** Merge fields (e.g. allowedPages) into selectedEvent + matching events[] entry */
    patchSelectedEvent: (state, action) => {
      const patch = action.payload || {};
      if (state.selectedEvent) {
        state.selectedEvent = { ...state.selectedEvent, ...patch };
      }
      if (patch.eventId || state.selectedEvent?.eventId) {
        const id = String(patch.eventId || state.selectedEvent.eventId);
        state.events = (state.events || []).map((event) =>
          String(event.eventId) === id ? { ...event, ...patch } : event
        );
      }
      if (state.user && patch.role) {
        state.user = { ...state.user, role: patch.role };
      }
    },

    // Optional: clear only the selected event
    clearSelectedEvent: (state) => {
      state.selectedEvent = null;
    },
  },
});

// Export actions to be dispatched
export const {
  loginRequest,
  loginSuccess,
  loginFailure,
  logout,
  updateUser,
  setSelectedEvent,
  patchSelectedEvent,
  clearSelectedEvent,
} = authSlice.actions;

// Export the reducer, which will be used in the store configuration
export default authSlice.reducer;

// Optional: Add rehydration check if the `user` is present in localStorage
export const checkRehydration = (state) => state.auth.user;
